import React, { useEffect, useState, useContext } from 'react';
import { GoogleMap, InfoWindow, Marker, Polyline } from '@react-google-maps/api';
import GoogleMapContext from './GoogleMapContext';

function GoogleMapComponent({ nestedLevelCoordinates, withMultiCoordinates, locations = [], showMapImage }) {
  const { googleScriptLoaded, currentCoordinates } = useContext(GoogleMapContext);
  const [map, setMap] = useState(null); // Hold map instance
  const [activeMarker, setActiveMarker] = useState(null); // Track active marker for InfoWindow

  const mapContainerStyle = {
    height: '300px',
    width: '100%',
    borderRadius: 20,
    marginBottom: 24,
  };

  const polylineOptions = {
    strokeOpacity: 0,
    strokeWeight: 2,
    icons: [
      {
        icon: {
          path: 'M 0,-1 0,1',
          strokeOpacity: 1,
          scale: 3,
        },
        offset: '0',
        repeat: '10px',
      },
    ],
  };

  useEffect(() => {
    if (map && (nestedLevelCoordinates?.length || locations.length > 0) && !showMapImage) {
      const bounds = new window.google.maps.LatLngBounds();

      // If only a single location exists
      if (withMultiCoordinates && locations?.length === 1) {
        const singleLocation = locations[0];
        map.setCenter(new window.google.maps.LatLng(singleLocation.lat, singleLocation.lng));
        map.setZoom(15); // Set a zoom level for a wider view
        return; // Exit early to avoid fitting bounds
      }

      // Extend bounds for nestedLevelCoordinates
      const extendBoundsForNestedPolylines = (polyline) => {
        if (polyline?.lat && polyline?.lng) {
          bounds.extend(new window.google.maps.LatLng(polyline.lat, polyline.lng));
        }
        // Extend for each tier dynamically
        Object.keys(polyline)?.forEach((key) => {
          if (key.startsWith('endPointsTier')) {
            polyline[key]?.forEach((endPoint) => extendBoundsForNestedPolylines(endPoint));
          }
        });
      };

      nestedLevelCoordinates?.forEach((polyline) => {
        extendBoundsForNestedPolylines(polyline.startPointTier0);
      });

      // Extend bounds for additional locations if needed
      if (withMultiCoordinates && locations.length > 0) {
        locations?.forEach((location) => {
          if (location.lat && location.lng) {
            bounds.extend(new window.google.maps.LatLng(location.lat, location.lng));
          }
        });
      }

      // Fit map to bounds for multiple coordinates
      map.fitBounds(bounds);
    }
  }, [map, locations, withMultiCoordinates, nestedLevelCoordinates, showMapImage]);

  const handleMarkerClick = (location) => {
    if (activeMarker === location) {
      // Close the InfoWindow if the same marker is clicked
      setActiveMarker(null);
    } else {
      // Open the InfoWindow for the clicked marker
      setActiveMarker(location);
    }
  };

  const generateStaticMapUrlNestedLvelCoordinates = () => {
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY; // Replace with your actual Google Maps API key
    const size = '800x400'; // Set a larger size for better aspect ratio
    const scale = 2; // Use scale=2 for high-resolution images

    // Build markers and paths for each tier of coordinates
    const markers = [];
    const paths = [];

    // Function to recursively traverse each tier
    const addMarkersAndPaths = (tier, level, pathStart = null) => {
      const { lat, lng } = tier;

      // Add marker for the current tier with the level number as label
      markers.push(`markers=label:${level}|${lat},${lng}`);

      // If a previous tier exists, add path from previous tier to current
      if (pathStart) {
        paths.push(`path=color:0xff0000ff|weight:2|${pathStart.lat},${pathStart.lng}|${lat},${lng}`);
      }

      // Recursively add markers and paths for next level
      const nextTierKey = `endPointsTier${level + 1}`;
      const nextTier = tier[nextTierKey];
      if (nextTier) {
        nextTier.forEach((nextTierPoint) => {
          addMarkersAndPaths(nextTierPoint, level + 1, { lat, lng }); // Recursively go deeper
        });
      }
    };

    // Loop through the nested data
    nestedLevelCoordinates?.forEach((tier0, index0) => {
      const { startPointTier0 } = tier0;
      addMarkersAndPaths(startPointTier0, 0); // Start recursion from Tier 0
    });

    const markersParam = markers.join('&');
    const pathsParam = paths.join('&');

    // Construct the final URL
    return `${baseUrl}?size=${size}&scale=${scale}&${markersParam}&${pathsParam}&key=${apiKey}`;
  };

  const generateStaticMapUrlForMutliCoorinates = () => {
    const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY; // Replace with your actual API key
    const size = '800x400'; // Set map image dimensions

    const scale = 2;
    // Prepare markers
    const markers = locations.map((location) => `markers=${location.lat},${location.lng}`).join('&');

    // Prepare paths for nested coordinates if needed
    const path = nestedLevelCoordinates
      ?.map((tier0) => {
        const points = [{ lat: tier0.startPointTier0.lat, lng: tier0.startPointTier0.lng }]
          .concat(tier0.startPointTier0.endPointsTier1.map((tier1) => ({ lat: tier1.lat, lng: tier1.lng })))
          .map((point) => `${point.lat},${point.lng}`)
          .join('|');
        return `path=color:red|weight:2|${points}`;
      })
      .join('&');

    return `${baseUrl}?size=${size}&scale=${scale}&${markers}&${path}&key=${apiKey}`;
  };
  const renderPolylines = () => {
    return nestedLevelCoordinates?.map((tier0, index0) => {
      const { startPointTier0 } = tier0;

      // Recursive function to render each tier's markers and polylines
      const renderPolylinesForTier = (tier, currentTierLevel) => {
        const nextTier = tier[`endPointsTier${currentTierLevel + 1}`];
        const currentlevel = currentTierLevel + 1;
        // Define colors for each tier transition
        const polylineColors = ['#FF0000', '#FF0000', '#FF0000'];
        return (
          <>
            {nextTier?.map((nextTierPoint, index) => {
              const polylineColor = polylineColors[currentTierLevel] || '#FF0000';
              return (
                <React.Fragment key={`tier-${currentTierLevel}-${index0}-${index}`}>
                  <Marker position={nextTierPoint} label={{ text: `${currentlevel}`, color: '#000', fontSize: '14px', fontWeight: 'bold' }} />
                  <Polyline
                    path={[
                      { lat: tier.lat, lng: tier.lng },
                      { lat: nextTierPoint.lat, lng: nextTierPoint.lng },
                    ]}
                    options={{ ...polylineOptions, strokeColor: polylineColor }}
                  />
                  {renderPolylinesForTier(nextTierPoint, currentlevel)} {/* Recursively render next tier */}
                </React.Fragment>
              );
            })}
          </>
        );
      };

      return (
        <React.Fragment key={`tier0-${index0}`}>
          <Marker position={startPointTier0} label={{ text: '0', color: '#000', fontSize: '14px', fontWeight: 'bold' }} />
          {renderPolylinesForTier(startPointTier0, 0)} {/* Start recursion from Tier 0 */}
        </React.Fragment>
      );
    });
  };

  // Display static map if Google Maps API script is not loaded or if a simple snapshot is needed
  if (withMultiCoordinates && showMapImage) {
    return <img src={generateStaticMapUrlForMutliCoorinates()} alt="Map snapshot" style={mapContainerStyle} />;
  }
  if (nestedLevelCoordinates?.length && showMapImage) {
    return <img src={generateStaticMapUrlNestedLvelCoordinates()} alt="Map snapshot" style={mapContainerStyle} />;
  }
  const defaultCenter = { lat: currentCoordinates?.lat || 0, lng: currentCoordinates?.lng || 0 };

  return googleScriptLoaded ? (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={locations?.length > 0 ? undefined : defaultCenter}
      zoom={locations?.length > 0 ? 12 : 10}
      onLoad={(mapInstance) => setMap(mapInstance)}
    >
      {withMultiCoordinates &&
        locations?.map((location, index) => (
          <Marker key={index} position={{ lat: location.lat, lng: location.lng }} onClick={() => handleMarkerClick(location)}>
            {activeMarker === location && (
              <InfoWindow position={location} onCloseClick={() => setActiveMarker(null)}>
                <div>{location.name || 'No name available'}</div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      {renderPolylines()}
    </GoogleMap>
  ) : null;
}

export default GoogleMapComponent;
